import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSerials } from '../../../hooks';
import { useAuth } from '../../../Auth';
import { apiFetch } from '../../../lib/api/client';

/** Reactive min-width media query (inline sidebar ↔ mobile drawer switching). */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

interface SidebarProps {
  /** Drawer visibility below md screens (PrivateLayout owns the state). */
  mobileOpen?: boolean;
  /** Close handler for the mobile drawer. */
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: serials = [], isLoading: serialsLoading } = useSerials();
  const { isAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  // Billing/plan state
  const [planName, setPlanName] = useState<string | null>(null);
  const [planActive, setPlanActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchBilling() {
      try {
        const accounts = await apiFetch<{ planId: string; active: boolean }[]>('/api/billing/accounts');
        if (!cancelled && accounts.length > 0) {
          setPlanName(accounts[0].planId);
          setPlanActive(accounts[0].active);
        }
      } catch {
        // billing unavailable – keep defaults
      }
    }
    fetchBilling();
    return () => { cancelled = true; };
  }, []);

  const planLabel = planName === 'pro'
    ? 'PRO PLAN'
    : planName === 'free'
      ? 'TRIAL'
      : planName
        ? planName.toUpperCase()
        : 'FREE PLAN';
  const isPro = planActive && planName === 'pro';

  // Persist collapse state
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved !== null) {
      setCollapsed(saved === 'true');
    }
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar-collapsed', String(next));
  };

  // Close the mobile drawer when a route changes — it follows navigation.
  useEffect(() => {
    if (mobileOpen) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Collapse only applies to the inline (md+) sidebar; the mobile drawer is
  // always fully expanded.
  const isDesktop = useIsDesktop();
  const mobileCapable = mobileOpen !== undefined;
  const collapsedView = mobileCapable && isDesktop ? collapsed : false;

  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) =>
    location.pathname.startsWith(prefix);

  const mainNavItems = [
    { label: 'Dashboard', icon: 'grid_view', path: '/home' },
    { label: 'Shows', icon: 'auto_stories', path: '/home/serials' },
    { label: 'Palette', icon: 'palette', path: '/home/palette' },
    { label: 'Notes', icon: 'sticky_note_2', path: '/home/notes' },
    { label: 'Settings', icon: 'tune', path: '/home/settings' }
  ];

  const adminNavItems = [
    { label: 'Overview', icon: 'grid_view', path: '/home/admin' },
    { label: 'User Management', icon: 'people', path: '/home/admin/users' },
    { label: 'Plans & Billing', icon: 'credit_card', path: '/home/admin/plans' },
    { label: 'System Logs', icon: 'terminal', path: '/home/admin/logs' },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {mobileCapable && mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      <aside
        aria-label="Primary navigation"
        className={`${collapsedView ? 'w-16' : 'max-md:w-64 md:w-64'} h-full flex flex-col bg-surface-container-low border-r border-outline-variant/20 font-body overflow-hidden max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:shadow-2xl max-md:transition-transform max-md:duration-300 ${
          mobileOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full'
        }`}
      >
        {/* Collapse Toggle (md+ only) */}
        <div
          className={`max-md:hidden flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 py-3 border-b border-outline-variant/10`}
        >
          {!collapsedView && (
            <p className="text-[0.6rem] font-mono text-on-surface-variant/40 uppercase tracking-[0.2em]">
              Navigation
            </p>
          )}
          <button
            onClick={toggleCollapsed}
            className="btn btn-ghost btn-xs text-on-surface-variant/40 hover:text-on-surface p-1.5"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="material-icons text-sm">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>

        <div
          className={`flex-1 overflow-y-auto ${collapsedView ? 'px-1.5 py-2' : 'px-3 py-2'} space-y-6 custom-scrollbar max-md:pb-[max(1rem,env(safe-area-inset-bottom))]`}
        >
          {/* Main Navigation */}
          <section>
            {!collapsedView && (
              <p className="px-3 text-[0.6rem] font-mono text-on-surface-variant/40 uppercase tracking-[0.2em] mb-3">
                Main Menu
              </p>
            )}
            <nav className="flex flex-col gap-0.5">
              {mainNavItems.map((item) => {
                const active =
                  isActive(item.path) ||
                  (item.path !== '/home' &&
                    isActivePrefix(item.path) &&
                    !mainNavItems.some(
                      (n) =>
                        n !== item &&
                        n.path !== '/home' &&
                        isActivePrefix(n.path) &&
                        n.path.length > item.path.length
                    ));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center gap-3 py-3 md:py-2.5 px-3 transition-all duration-150 ${
                      active
                        ? 'bg-tertiary text-on-tertiary'
                        : 'text-on-surface-variant/70 hover:bg-surface-container hover:text-on-surface'
                    } ${collapsedView ? 'justify-center' : ''}`}
                    title={collapsedView ? item.label : undefined}
                  >
                    <span
                      className={`material-icons text-lg ${active ? 'text-on-tertiary' : 'text-on-surface-variant/50'}`}
                    >
                      {item.icon}
                    </span>
                    {!collapsedView && (
                      <span className="font-medium text-sm tracking-tight">
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </section>

          {/* Shows Sub-menu */}
          <section>
            {!collapsedView && (
              <div className="flex items-center justify-between px-3 mb-3">
                <p className="text-[0.6rem] font-mono text-on-surface-variant/40 uppercase tracking-[0.2em]">
                  Your Shows
                </p>
              </div>
            )}
            {collapsedView && (
              <div className="flex justify-center mb-2">
                <span className="material-icons text-on-surface-variant/30 text-sm">
                  auto_stories
                </span>
              </div>
            )}

            <nav className="flex flex-col gap-0.5">
              {serialsLoading
                ? !collapsedView
                  ? [1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-8 mx-3 bg-surface-container animate-pulse mb-1"
                      />
                    ))
                  : [1, 2].map((i) => (
                      <div
                        key={i}
                        className="h-8 mx-1 bg-surface-container animate-pulse mb-1"
                      />
                    ))
                : serials.length === 0
                  ? !collapsedView && (
                      <div className="mx-3 py-4 text-center">
                        <span className="material-icons text-on-surface-variant/20 text-xl block mb-1">
                          auto_stories
                        </span>
                        <p className="text-[0.65rem] text-on-surface-variant/40 mb-2 font-mono">
                          No shows yet
                        </p>
                        <Link
                          to="/home"
                          className="text-[0.6rem] text-tertiary/60 hover:text-tertiary font-mono uppercase tracking-wider"
                        >
                          Create one →
                        </Link>
                      </div>
                    )
                  : serials.map((serial) => {
                      const active = isActive(`/home/serials/${serial.id}`);
                      return (
                        <Link
                          key={serial.id}
                          to={`/home/serials/${serial.id}`}
                          onClick={onClose}
                          className={`flex items-center gap-3 py-3 md:py-2 px-3 transition-all duration-150 ${
                            active
                              ? 'bg-surface-container text-tertiary border-l-2 border-tertiary'
                              : 'text-on-surface-variant/40 hover:bg-surface-container hover:text-on-surface'
                          } ${collapsedView ? 'justify-center' : ''}`}
                          title={collapsedView ? serial.key : undefined}
                        >
                          <div
                            className={`w-1.5 h-1.5 flex-shrink-0 ${active ? 'bg-tertiary' : 'bg-on-surface-variant/15'}`}
                          />
                          {!collapsedView && (
                            <span className="font-medium truncate text-sm">
                              {serial.key}
                            </span>
                          )}
                        </Link>
                      );
                    })}
            </nav>
          </section>

          {/* Admin Section */}
          {isAdmin && (
            <section>
              {!collapsedView && (
                <p className="px-3 text-[0.6rem] font-mono text-tertiary/60 uppercase tracking-[0.2em] mb-3">
                  System Admin
                </p>
              )}
              {collapsedView && (
                <div className="flex justify-center mb-2">
                  <span className="material-icons text-tertiary/40 text-sm">
                    admin_panel_settings
                  </span>
                </div>
              )}
              <nav className="flex flex-col gap-0.5">
                {adminNavItems.map((item) => {
                  const active = isActivePrefix(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={`flex items-center gap-3 py-3 md:py-2.5 px-3 transition-all duration-150 ${
                        active
                          ? 'bg-tertiary/15 text-tertiary border-l-2 border-tertiary'
                          : 'text-on-surface-variant/50 hover:bg-surface-container hover:text-on-surface'
                      } ${collapsedView ? 'justify-center' : ''}`}
                      title={collapsedView ? item.label : undefined}
                    >
                      <span
                        className={`material-icons text-lg ${active ? 'text-tertiary' : 'text-on-surface-variant/40'}`}
                      >
                        {item.icon}
                      </span>
                      {!collapsedView && (
                        <span className="font-medium text-sm tracking-tight">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </section>
          )}
        </div>

        {/* Sidebar Footer */}
        {!collapsedView && (
          <div className="p-4 mt-auto border-t border-outline-variant/20 max-md:pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="bg-surface-container p-4 border border-outline-variant/10">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[0.6rem] font-mono text-on-surface font-bold uppercase tracking-widest">
                  {planLabel}
                </p>
                {planActive && (
                  <span className="text-[0.5rem] font-mono uppercase bg-tertiary/10 text-tertiary px-1.5 py-0.5 border border-tertiary/20">
                    Active
                  </span>
                )}
              </div>
              {isPro ? (
                <p className="text-[0.6rem] text-on-surface-variant/40 leading-relaxed font-mono">
                  Full access to all features.
                </p>
              ) : (
                <p className="text-[0.6rem] text-on-surface-variant/40 leading-relaxed font-mono">
                  Unlock advanced collaboration features.
                </p>
              )}
              {!isPro && (
                <button
                  onClick={() => navigate('/home/settings/billing')}
                  className="btn btn-primary btn-sm mt-3 font-bold px-3"
                >
                  Upgrade
                </button>
              )}
            </div>
          </div>
        )}
        {collapsedView && (
          <div className="py-3 px-1.5 flex justify-center border-t border-outline-variant/20">
            <button
              onClick={toggleCollapsed}
              className="btn btn-ghost btn-xs text-on-surface-variant/40 hover:text-on-surface p-1.5"
              title="Expand sidebar"
            >
              <span className="material-icons text-sm">chevron_right</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;